import { useEffect, useMemo, useState } from 'react';
import {
  ControllerAlert as Alert,
  Divider,
  Box,
  Switch,
  FormControlLabel,
  GridColDef,
  ControllerDataGrid as DataGrid,
  DataGridNoResultsOverlay,
  Link,
  Typography,
} from '@bymason-private/legacy-pattern-components';
import {
  DeviceModel,
  SimModel,
  useGetSimConnectivity,
  useUpdateSimStatus,
  SimType,
  UpdateSimStatusSubet,
} from '@bymason/controller-api-hooks';
import { useAuth } from '@bymason-private/auth-provider';

import {
  getSimEnabledDisabledStatus,
  getUsageDataDateRange,
  getUsageDataGridRows,
  isSimEnabled,
  isSimPending,
  TOTALS_ID,
} from './logic';
import { Route, RouterLink } from '@bymason-private/route-provider';
import { dataGridDataSizeFormatter } from '@bymason-private/pattern-components';
import {
  useToast,
  LegacyDetailsList,
  DetailItem,
  DetailsListEmpty,
  UpdateSimStatus,
} from '@bymason-private/controller-shared-widgets';

const usageGridCols: GridColDef[] = [
  {
    field: 'package',
    headerName: 'Applications',
    flex: 3,
  },
  {
    field: 'mobile',
    headerName: 'Cellular',
    type: 'number',
    valueFormatter: dataGridDataSizeFormatter,
    flex: 1,
  },
  {
    field: 'wifi',
    headerName: 'Wi-Fi',
    type: 'number',
    valueFormatter: dataGridDataSizeFormatter,
    flex: 1,
  },
];

const subSectionSx = {
  display: 'flex',
  '& > *': {
    flex: '1 1 auto',
  },
};

const subSectionTitleSx = {
  flex: '0 0 120px',
  fontWeight: '700',
  padding: '9px',
  overflowX: 'hidden',
};

export function ConnectivityInfoView({ deviceInfo, connectivityViewRoute }: ConnectivityInfoViewProps) {
  const { authToken = '' } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();

  const [getSimsStatus, simsStatusResult, , simsStatusBusy] = useGetSimConnectivity(authToken);
  const [
    updateStatus,
    updateStatusResult,
    updateStatusError,
    updateStatusBusy,
    ,
    ,
    clearUpdateStatusResult,
  ] = useUpdateSimStatus(authToken);
  const [simStatusOptions, setSimStatusOptions] = useState<SimStatusOptions | null>(null);
  const [simsStatusInfo, setSimsStatusInfo] = useState<(void | SimModel | null)[]>([]);

  const handleSimStatusClick = (iccid: string, simType: SimType, enable: boolean) => () => {
    const newStatus = getSimEnabledDisabledStatus(simType, enable);
    if (iccid) {
      setSimStatusOptions({ iccid, state: newStatus });
    }
  };

  const handleSimStatusDialogClose = () => {
    setSimStatusOptions(null);
  };

  const handleSimStatusDialogConfirm = () => {
    if (simStatusOptions) {
      updateStatus({ requests: [simStatusOptions] });
    }
  };

  const usageRows = useMemo(() => {
    if (deviceInfo?.connection.dataUsage) {
      return getUsageDataGridRows(deviceInfo?.connection.dataUsage);
    }
    return null;
  }, [deviceInfo?.connection.dataUsage]);

  const usageDateRange = useMemo(() => {
    if (deviceInfo?.connection.dataUsage) {
      return getUsageDataDateRange(deviceInfo?.connection.dataUsage);
    }
    return null;
  }, [deviceInfo?.connection.dataUsage]);

  const simItems = useMemo<DetailItem[][]>(() => {
    const sims = deviceInfo?.connection.sim || [];
    return sims.map((sim, idx) => {
      const simInfo = simsStatusInfo[idx] as void | SimModel | null;
      const carrier = sim?.carrier || 'Unknown';
      const iccid = sim?.iccid || '';
      const iccidLink =
        iccid && !!simInfo && connectivityViewRoute ? (
          <RouterLink
            linkComponent={<Link sx={{ overflowWrap: 'anywhere' }}>{iccid}</Link>}
            route={connectivityViewRoute}
            state={{ iccid }}
          />
        ) : (
          <span>{iccid || 'Unavailable'}</span>
        );

      const mnc = sim?.mnc ?? 'Unavailable';
      const mcc = sim?.mcc ?? 'Unavailable';
      const countryIso = sim?.countryiso ?? 'Unavailable';

      let status: React.ReactNode = 'Unavailable';
      if (simInfo && isSimPending(simInfo.status)) {
        status = simInfo.status;
      } else if (simInfo) {
        const simEnabled = isSimEnabled(simInfo.simType, simInfo.status);
        const switchLabel = simEnabled ? 'Enabled' : 'Disabled';
        status = (
          <FormControlLabel
            control={
              <Switch
                checked={simEnabled}
                onChange={handleSimStatusClick(iccid, simInfo.simType, !simEnabled)}
              />
            }
            label={switchLabel}
            sx={{ height: '20px', '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
          />
        );
      }

      return [
        { label: `Carrier ${idx + 1}`, value: carrier },
        { label: 'ICCID', value: iccidLink },
        { label: 'MNC', value: mnc },
        { label: 'MCC', value: mcc },
        { label: 'Country ISO', value: countryIso },
        { label: 'Status', value: status },
      ];
    });
  }, [deviceInfo?.connection.sim, connectivityViewRoute, simsStatusInfo]);

  const wifiItems = useMemo<DetailItem[]>(() => {
    const wifi = deviceInfo?.connection.wifi;
    const status = wifi?.enabled ? 'Enabled' : 'Disabled';
    const ssid = wifi?.ssid ?? 'Unavailable';
    const mac = wifi?.adapterMacAddress ?? 'Unavailable';
    const ip = wifi?.ipaddress ?? 'Unavailable';

    return [
      { label: 'Status', value: status },
      { label: 'SSID', value: ssid },
      { label: 'MAC address', value: mac },
      { label: 'IP Address', value: ip },
    ];
  }, [deviceInfo]);

  useEffect(() => {
    const sims = deviceInfo?.connection.sim || [];
    if (sims || updateStatusResult) {
      const iccidList = sims.map((s) => s.iccid).filter((i) => typeof i !== 'undefined') as string[];
      getSimsStatus({ iccidList });
    }
  }, [deviceInfo?.connection.sim, getSimsStatus, updateStatusResult]);

  useEffect(() => {
    if (!simsStatusBusy && simsStatusResult) {
      const newStatusInfo = simsStatusResult.map((result) => {
        if (result.status === 'fulfilled') {
          return result.value;
        } else {
          return null;
        }
      });
      setSimsStatusInfo(newStatusInfo);
    }
  }, [simsStatusBusy, simsStatusResult]);

  useEffect(() => {
    if (simStatusOptions && updateStatusResult && !updateStatusBusy && !updateStatusError) {
      setToastSeverity('success');
      setToastMessage(
        `Your request to ${
          simStatusOptions.state === 'active' ? 'enable' : 'disable'
        } the SIM has been queued and should complete momentarily. Please click refresh button to check for new status`
      );
      setSimStatusOptions(null);
      clearUpdateStatusResult && clearUpdateStatusResult();
    }
  }, [
    clearUpdateStatusResult,
    setToastMessage,
    setToastSeverity,
    simStatusOptions,
    updateStatusBusy,
    updateStatusError,
    updateStatusResult,
  ]);

  return (
    <div>
      <Box paddingTop={2} component='section'>
        <Box sx={subSectionSx}>
          <Typography variant='body2' sx={subSectionTitleSx}>
            SIM
          </Typography>
          {simItems.length > 0 ? (
            simItems.map((items, idx) => (
              <LegacyDetailsList key={`sim-${idx}`} hideIconsColumn items={items} />
            ))
          ) : (
            <DetailsListEmpty content='Unavailable' />
          )}
        </Box>
        <Box sx={subSectionSx}>
          <Typography variant='body2' sx={subSectionTitleSx}>
            Wi-Fi
          </Typography>
          <LegacyDetailsList hideIconsColumn items={wifiItems} />
        </Box>
        <Box sx={subSectionSx}>
          <Typography variant='body2' sx={subSectionTitleSx}>
            Bluetooth
          </Typography>
          <Box>
            {deviceInfo?.connection.bluetooth.enabled ? (
              <DetailsListEmpty content='Enabled' />
            ) : (
              <DetailsListEmpty content='Unavailable' />
            )}
          </Box>
        </Box>
      </Box>
      <Box paddingTop={2} component='section'>
        <Box marginBottom={2}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant='h5'>Network utilization</Typography>
          {usageDateRange && (
            <LegacyDetailsList items={[{ type: 'date', label: 'Date Range', value: usageDateRange }]} />
          )}
          <Box paddingTop={2} maxWidth='1024px'>
            {deviceInfo && usageRows?.apps && usageRows?.totals && (
              <>
                <Alert severity='info' variant='outlined'>
                  {`Carrier's usage total may differ from device's usage total`}
                </Alert>
                <Box paddingTop={2}>
                  <DataGrid
                    sx={{
                      marginBottom: 8,
                      [`& .MuiDataGrid-row.${TOTALS_ID}`]: {
                        fontWeight: 700,
                      },
                    }}
                    getRowClassName={(params) => (params.id === TOTALS_ID ? TOTALS_ID : '')}
                    autoHeight
                    disableVirtualization
                    density='compact'
                    columns={usageGridCols}
                    rows={[...usageRows.apps, usageRows.totals]}
                    components={{ NoRowsOverlay: DataGridNoResultsOverlay }}
                    componentsProps={{ noRowsOverlay: { text: 'No application data found' } }}
                  />
                </Box>
              </>
            )}
            {deviceInfo && !usageRows?.apps && usageRows?.totals && (
              <>
                <Alert severity='warning' variant='outlined'>
                  {'If you are not seeing any data, you may need to update Mason OS to 2.2.0 or greater'}
                </Alert>
                <Box paddingTop={2}>
                  <DataGrid
                    sx={{
                      marginBottom: 8,
                      [`& .MuiDataGrid-row.${TOTALS_ID}`]: {
                        fontWeight: 700,
                      },
                    }}
                    getRowClassName={(params) => (params.id === TOTALS_ID ? TOTALS_ID : '')}
                    autoHeight
                    disableVirtualization
                    density='standard'
                    columns={usageGridCols}
                    rows={[usageRows.totals]}
                    components={{ NoRowsOverlay: DataGridNoResultsOverlay }}
                    componentsProps={{ noRowsOverlay: { text: 'No application data found' } }}
                  />
                </Box>
              </>
            )}
            {deviceInfo && !usageRows?.totals && (
              <Alert severity='info' variant='outlined'>
                {'No device data found'}
              </Alert>
            )}
          </Box>
        </Box>
      </Box>
      {simStatusOptions && (
        <UpdateSimStatus
          open={!!simStatusOptions}
          iccid={simStatusOptions.iccid}
          newStatus={simStatusOptions.state === 'active' ? 'enable' : 'disable'}
          onClose={handleSimStatusDialogClose}
          onSubmit={handleSimStatusDialogConfirm}
          error={updateStatusError}
          busy={updateStatusBusy}
        />
      )}
    </div>
  );
}

interface ConnectivityInfoViewProps {
  deviceInfo?: DeviceModel;
  connectivityViewRoute?: Route;
}

interface SimStatusOptions {
  iccid: string;
  state: UpdateSimStatusSubet;
}
